import {
  Box, Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  VStack
} from "@chakra-ui/react";
import React, { useContext, useEffect, useMemo, useState, } from "react";
import { useTranslation } from "react-i18next";
import Draggable from "react-draggable";
import { FaRegQuestionCircle } from "react-icons/fa";
import {
  competitiveEstimate,
  equipmentComparisonItems,
  equipmentType,
  EstimateCheckListCategory,
  EstimateCheckListTextMaxLength,
  inHouseReview,
  Messages,
  ourProductUsageStatus,
  pastPerformance,
  paymentType,
  procurementApproval,
  reverseAuction,
  supplier,
  ValidDateRangeEnd,
  ValidDateRangeStart
} from '../../contexts/constants';
import { Currency, EquipmentComparison, Errors, EstimateCheckListFormType, PagePermissions } from '../../contexts/types';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ProjectContext } from '../InvestmentScreening/InvestmentScreeningDetail';
import '../common.css';
import { CustomNumberInput } from "../InvestmentScreening/CustomNumberInput";
import CurrencyList from "../InvestmentScreening/CurrencyList";
import { isValidDateRange, isValidDateFormat, formatNumber, formatDateByLocale } from "../../utils/utils";
import Decimal from "decimal.js";
import GoogleAPI from "../../utils/GoogleAPI";
import { useEstimateCheckListSpreadSheet } from "../../hooks/useEstimateCheckListSpreadSheet";
import ConfirmModal from "../../components/ConfirmModal";

/**
 * チェックリストパラメータ
 */
interface EstimateCheckListProps {

  /** プロジェクトID */
  projectId?: string | null;
  /** 設備ID */
  equipmentId?: string | null;
  /** 設備名 */
  equipmentName?: string | null;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** チェックリスト更新状態 */
  setCheckListUpdateStatus?: React.Dispatch<React.SetStateAction<boolean>>;
  /** 通貨マスタ */
  currencyData?: Currency[];

}

/**
 * 見積チェックリスト
 */
const EstimateCheckList: React.FC<EstimateCheckListProps> = ({ projectId, equipmentId, equipmentName, pagePermissions, setCheckListUpdateStatus, currencyData }) => {

  const { t } = useTranslation();
  /** トースト表示用 */
  const toast = useToast();
  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 振り返りモード */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;

  /** ポップアップ横幅 */
  const POPUP_WIDTH_RATIO = 90;
  const POPUP_WIDTH = `${POPUP_WIDTH_RATIO}vw`;
  /** ポップアップ縦幅 */
  const POPUP_HEIGHT_RATIO = 85;
  const POPUP_HEIGHT = `${POPUP_HEIGHT_RATIO}vh`;
  /** 画面タイトル */
  const TITLE = t('components.EstimateCheckList.title');
  /** 内製 */
  const IN_HOUSE = "MMI内製";

  /** 最大文字数：会社名 */
  const COMPANYNAME_TEXT_MAX_LENGTH = 300;

  /** FormLabelスタイル */
  const formLabelStyle = {
    width: "250px",
    wordBreak: "break-word" as const,
  }
  /** FormErrorMessageスタイル */
  const formErrorMessageStyle = {
    marginTop: "0px",
    fontWeight: "bold",
  }

  /** 見積チェックリスト初期値 */
  const defaultEstimateCheckList = {

    // 設備種類
    equipmentType: {
      equipmentType: equipmentType.genericEquipment.value,
    },
    // リバースオークション有無
    reverseAuction: {
      reverseAuction: reverseAuction.isTrue.value,
    },
    // 内製検討
    inHouseReview: {
      inHouseReview: inHouseReview.isTrue.value,
      inHouseReviewNoReason: null,
    },
    // 相見積
    competitiveEstimate: {
      competitiveEstimate: competitiveEstimate.isTrue.value,
      competitiveEstimateNoReason: null,
    },
    // 過去実績
    pastPerformance: {
      pastPerformance: pastPerformance.isTrue.value,
      purchaseDate: null,
      purchasePrice: null,
    },
    // 発注先
    supplier: {
      supplier: supplier.manufacturer.value,
      distributorReason: null,
      orderDate: null,
    },
    // 価格交渉の回数
    negotiation: {
      negotiationCount: null,
      negotiationBackground: null,
    },
    // 支払条件
    paymentType: {
      advancePayment: paymentType.isTrue.value,
      paymentBackground: null,
      procurementApproval: null,
    },
    // 当社製品使用の確認/要請
    ourProductUsage: {
      usageStatus: ourProductUsageStatus.used.value,
      productName: null,
    },
    // 外注を使用せず購入する理由
    purchaseWithoutOutsourcing: {
      reason: null,
    },
    // 相見積による比較
    equipmentComparison: {
      equipmentComparison: [],
      superiority: Array.from({ length: 10 }, () => ""),
    },
    // 選定理由
    selectReason: {
      selectReason: null,
    },
    // 通貨
    currency: {
      currencyId: "currency001",
      currencyName: "JPY",
      exchangeRate: 1,
    },

  }

  /** 相見積による比較初期値 */
  const defaultEquipmentComparison = {

    // 会社名
    companyName: null,
    // 本体価格
    basePrice: null,
    // 台数
    unit: null,
    // オプション価格
    optionPrice: null,
    // 特徴
    features: null,
    // 総金額
    totalPrice: null,
    // 納期
    deadline: null,
    // 精度/品質
    quality: equipmentComparisonItems.quality.none.value,
    // サービス体制
    system: equipmentComparisonItems.system.none.value,
    // リピート
    repeat: equipmentComparisonItems.repeat.none.value,
    // 課題
    issue: null,

  }

  // ------------------------------
  // useState
  // ------------------------------

  // #region useState

  /** 画面表示制御 */
  const [isOpen, setIsOpen] = useState(false);
  /** 画面表示位置 */
  const [position, setPosition] = useState({ x: 0, y: 0 });
  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>();
  /** 相見積による比較エラー情報 (会社インデックス -> フィールド名 -> エラー有無) */
  const [equipmentComparisonErrors, setEquipmentComparisonErrors] = useState<{ [companyIndex: number]: { [fieldName: string]: boolean } }>({});
  /** 見積チェックリスト */
  const [estimateCheckListFormValue, setEstimateCheckListFormValue] = useState<EstimateCheckListFormType>(defaultEstimateCheckList);
  /** 相見積による比較 */
  const [equipmentComparison, setEquipmentComparison] = useState<EquipmentComparison[]>([]);
  /** 会社名テキストボックス */
  const [companyNameText, setCompanyNameText] = useState<string>("");
  /** データ連携済みフラグ（GoogleSpreadシート作成直後はfalse、データ連携成功でtrue） */
  const [isDataLinked, setIsDataLinked] = useState<boolean>(true);
  /** シート再作成確認ダイアログの表示制御 */
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState<boolean>(false);
  /** モーダルを開いた際に遅延取得した見積チェックリスト本文（サイズ対策：一覧側では取得しない） */
  const [fetchedEstimateCheckList, setFetchedEstimateCheckList] = useState<EstimateCheckListFormType | null>(null);
  /** モーダルを開いた際に遅延取得した更新日時（保存時の排他制御に使用） */
  const [fetchedUpdateDate, setFetchedUpdateDate] = useState<string | null>(null);
  /** 本文取得中フラグ */
  const [isLoadingCheckList, setIsLoadingCheckList] = useState<boolean>(false);

  // #endregion

  // ------------------------------
  // useEffect
  // ------------------------------

  // #region 初期表示

  /**
   * 初期表示
   * モーダルを開くたびに、その設備1件分の見積チェックリスト本文をAPIから取得する（一覧取得時は本文を持たないため）。
   */
  useEffect(() => {

    if (isOpen) {
      // 画面表示時：本文を遅延取得
      fetchEstimateCheckList();
    } else {
      // 画面非表示
      setEstimateCheckListFormValue(defaultEstimateCheckList);
      setFetchedEstimateCheckList(null);
      setFetchedUpdateDate(null);
    }

  }, [isOpen]);

  /**
   * 見積チェックリスト本文取得（設備1件分）
   */
  const fetchEstimateCheckList = async () => {

    const functionName = "fetchEstimateCheckList";

    try {

      setIsLoadingCheckList(true);

      const payload = {
        query: `
          query getCheckListByEquipmentId($projectId: String, $equipmentId: String, $reviewDate: String) {
            getCheckListByEquipmentId(projectId: $projectId, equipmentId: $equipmentId, reviewDate: $reviewDate) {
              estimateCheckList
              updateDate
            }
          }`,
        variables: {
          projectId: projectId,
          equipmentId: equipmentId,
          ...(reviewDate && { reviewDate })
        }
      };

      const response = await WebAPI.request(payload);
      const result = response?.data?.getCheckListByEquipmentId;

      let parsedEstimateCheckList: EstimateCheckListFormType | null = null;
      if (result?.estimateCheckList) {
        try {
          parsedEstimateCheckList = JSON.parse(result.estimateCheckList);
        } catch (error) {
          console.error(`estimate check list format error`, error);
        }
      }

      setFetchedEstimateCheckList(parsedEstimateCheckList);
      setFetchedUpdateDate(result?.updateDate ?? null);

      if (parsedEstimateCheckList) {
        // 再表示
        const loadedData = { ...parsedEstimateCheckList };
        // currencyフィールドがない場合はデフォルト値を設定（既存データとの互換性対応）
        if (!loadedData.currency) {
          loadedData.currency = {
            currencyId: null,
            currencyName: "JPY",
            exchangeRate: 1,
          };
        }
        // ourProductUsageフィールドがない場合はデフォルト値を設定（既存データとの互換性対応）
        if (!loadedData.ourProductUsage) {
          loadedData.ourProductUsage = {
            usageStatus: ourProductUsageStatus.used.value,
            productName: null,
          };
        }
        // purchaseWithoutOutsourcingフィールドがない場合はデフォルト値を設定（既存データとの互換性対応）
        if (!loadedData.purchaseWithoutOutsourcing) {
          loadedData.purchaseWithoutOutsourcing = {
            reason: null,
          };
        }
        setEstimateCheckListFormValue(loadedData);
      } else {
        // 初回表示
        setEstimateCheckListFormValue(defaultEstimateCheckList);
        addCompany(IN_HOUSE);
      }

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
    } finally {
      setIsLoadingCheckList(false);
    }

  };

  // #endregion

  // #region 相見積による比較更新

  /**
   * 相見積による比較更新
   */
  useEffect(() => {

    // 相見積による比較更新
    if (JSON.stringify(estimateCheckListFormValue.equipmentComparison.equipmentComparison) !== JSON.stringify(equipmentComparison)) {
      setEquipmentComparison(estimateCheckListFormValue.equipmentComparison.equipmentComparison);
    }

  }, [estimateCheckListFormValue]);

  // #endregion

  // ------------------------------
  // 通貨関連
  // ------------------------------

  // #region 通貨関連

  /**
   * 通貨マスタデータ（フォールバック対応）
   */
  const displayCurrencyData = currencyData && currencyData.length > 0
    ? currencyData
    : [{ currencyId: "currency001", currencyName: "JPY", exchangeRate: 1 }];

  /**
   * 通貨変更ハンドラ
   */
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCurrency = displayCurrencyData.find(c => c.currencyId === e.target.value);
    if (selectedCurrency) {
      setEstimateCheckListFormValue(prev => ({
        ...prev,
        currency: {
          currencyId: selectedCurrency.currencyId ?? null,
          currencyName: selectedCurrency.currencyName ?? null,
          exchangeRate: selectedCurrency.exchangeRate ?? 1,
        },
      }));
    }
  };

  /**
   * 日本円換算額計算
   */
  const calculateJpyAmount = (totalPrice: string | null): number => {
    if (!totalPrice) return 0;
    const exchangeRate = estimateCheckListFormValue?.currency?.exchangeRate ?? 1;
    return new Decimal(totalPrice).times(exchangeRate).toNumber();
  };

  // #endregion

  // ------------------------------
  // Googleスプレッドシート連携
  // ------------------------------

  // #region Googleスプレッドシート連携

  /** Google API インスタンス */
  const googleAPI = useMemo(() => new GoogleAPI(), []);

  /** Google API 初期化 */
  useEffect(() => {
    const initGapi = async () => {
      try {
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }
      } catch (error) {
        console.error("Google API 初期化エラー:", error);
      }
    };
    initGapi();
  }, [googleAPI]);

  const {
    state: spreadSheetState,
    handleGoogleSpread,
    handleManualClick,
    handleSyncDataWithConversion,
    cleanup: cleanupSpreadSheet,
  } = useEstimateCheckListSpreadSheet(googleAPI, toast, {
    projectId: projectId ?? "",
    equipmentId: equipmentId ?? "",
    equipmentName: equipmentName ?? "",
    currencyData: displayCurrencyData,
  });

  /**
   * 「GoogleSpread」ボタン押下
   * 既にシート作成済み・未連携の場合は再作成確認ダイアログを表示する。
   */
  const handleGoogleSpreadClick = () => {
    if (spreadSheetState.activeSpreadsheetId && !isDataLinked) {
      setShowRegenerateConfirm(true);
      return;
    }
    handleGoogleSpread({ formValue: estimateCheckListFormValue, currencyData: displayCurrencyData });
    setIsDataLinked(false);
  };

  /** シート再作成確認ダイアログ「再作成する」押下 */
  const handleConfirmRegenerate = () => {
    setShowRegenerateConfirm(false);
    handleGoogleSpread({ formValue: estimateCheckListFormValue, currencyData: displayCurrencyData });
    setIsDataLinked(false);
  };

  /**
   * 「データ連携」ボタン押下
   * シートを読み取り、画面のフォーム値へ反映して必須チェックを実行する。
   */
  const handleDataLink = async () => {
    const result = await handleSyncDataWithConversion(estimateCheckListFormValue);
    if (!result || !result.formValue) {
      return;
    }

    setEstimateCheckListFormValue(result.formValue);
    const hasValidationError = requiredCheck(result.formValue);
    setIsDataLinked(true);

    if (result.errors.length > 0 || hasValidationError) {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.syncCompleteWithErrors', { count: result.errors.length }),
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
    } else {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.syncCompleteSuccess'),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // #endregion

  // ------------------------------
  // 業務ロジック
  // ------------------------------

  // #region エラー情報初期化

  /**
   * エラー情報初期化
   */
  const clearErrors = () => {

    // エラー情報初期化
    setErrors(JSON.parse(JSON.stringify({

      // 内製検討なしの理由
      inHouseReviewNoReason: {
        isError: false,
        errorMessage: ""
      },
      // 相見積なしの理由
      competitiveEstimateNoReason: {
        isError: false,
        errorMessage: ""
      },
      // 直近購入時期
      purchaseDate: {
        isError: false,
        errorMessage: ""
      },
      // 取得単価
      purchasePrice: {
        isError: false,
        errorMessage: ""
      },
      // 商社・代理店の理由
      distributorReason: {
        isError: false,
        errorMessage: ""
      },
      // 発注時期
      orderDate: {
        isError: false,
        errorMessage: ""
      },
      // 価格交渉の回数
      negotiationCount: {
        isError: false,
        errorMessage: ""
      },
      // 価格交渉の経緯
      negotiationBackground: {
        isError: false,
        errorMessage: ""
      },
      // 支払条件
      paymentBackground: {
        isError: false,
        errorMessage: ""
      },
      // 調達物流部門担当役員の承認
      procurementApproval: {
        isError: false,
        errorMessage: ""
      },
      // 当社製品使用の確認/要請 - 使用製品名
      ourProductUsageProductName: {
        isError: false,
        errorMessage: ""
      },
      // 外注を使用せず購入する理由
      purchaseWithoutOutsourcingReason: {
        isError: false,
        errorMessage: ""
      },
      // 相見積による比較
      equipmentComparison: {
        isError: false,
        errorMessage: ""
      },
      // 選定理由
      selectReason: {
        isError: false,
        errorMessage: ""
      },
      // 相見積比較（会社名／特徴／課題の文字数超過）
      equipmentComparisonMaxLength: {
        isError: false,
        errorMessage: ""
      }

    })));

    // 相見積による比較エラー初期化
    setEquipmentComparisonErrors({});

  }

  // #endregion

  // #region チェックリスト更新

  /**
   * チェックリスト更新
   * @param path 更新項目パス
   * @param value 値
   */
  const changeEstimateCheckListFormValue = (path: string, value: any) => {

    // 変数宣言
    const keys = path.split('.');

    // チェックリスト更新
    setEstimateCheckListFormValue(prevState => {
      const updatedState = { ...prevState };
      let property = updatedState;
      let subProperty: any = null;
      keys.forEach((key: string, index) => {
        if (index === keys.length - 1) {
          subProperty[key as keyof typeof subProperty] = value;
        } else {
          subProperty = property[key as keyof typeof property];
        }
      });
      return updatedState;
    });

  };

  // #endregion

  // #region 相見積による比較更新

  /**
   * 相見積による比較更新
   * @param index 要素番号
   * @param path 更新項目パス
   * @param value 値
   */
  const changeEquipmentComparJson = (index: number, path: string, value: string) => {

    // 変数宣言
    const equipmentComparison =
      estimateCheckListFormValue.equipmentComparison.equipmentComparison;

    // 相見積による比較更新
    const updatedData = equipmentComparison.map((item, i) =>
      i === index ? { ...item, [path]: value } : item
    );
    changeEstimateCheckListFormValue("equipmentComparison.equipmentComparison", updatedData);

  };

  // #endregion

  // #region 優位性更新

  /**
   * 優位性更新
   * @param index 要素番号
   * @param value 値
   */
  const changeSuperiorityElement = (index: number, value: string) => {

    // 優位性更新
    setEstimateCheckListFormValue((prevState) => {

      // 値が存在しない場合は処理を抜ける
      if (!prevState.equipmentComparison.superiority) {
        return prevState;
      }

      // 引数の要素番号が範囲外の場合を抜ける
      if (index < 0 || index >= prevState.equipmentComparison.superiority.length) {
        console.error("Index out of bounds");
        return prevState;
      }

      // 優位性更新
      const updatedSuperiority = [...prevState.equipmentComparison.superiority];
      updatedSuperiority[index] = value;
      return {
        ...prevState,
        equipmentComparison: {
          ...prevState.equipmentComparison,
          superiority: updatedSuperiority,
        },
      };

    });

  };

  // #endregion

  // #region 会社情報追加

  /**
   * 会社情報追加
   * @param companyName 会社名
   */
  const addCompany = (companyName: string) => {

    // 変数宣言
    const equipmentComparison =
      estimateCheckListFormValue.equipmentComparison.equipmentComparison;

    // 会社名が既に存在する場合はトースト表示
    const isDuplicate =
      equipmentComparison.some((company) => company.companyName === companyName);
    if (isDuplicate) {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.duplicateCompany'),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      return;
    }

    // 会社情報が既に3件以上存在する場合はトースト表示
    if (equipmentComparison.length >= 3) {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.companyLimitExceeded'),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      return;
    }

    // 会社名の要素を追加
    const newCompany = { ...defaultEquipmentComparison, companyName: companyName };
    changeEstimateCheckListFormValue("equipmentComparison.equipmentComparison", [
      ...equipmentComparison,
      newCompany,
    ]);

  }

  // #endregion

  // #region 会社情報削除

  /**
   * 会社情報削除
   * @param companyName 会社名
   */
  const removeCompany = (companyName: string) => {

    // 変数宣言
    const equipmentComparison =
      estimateCheckListFormValue.equipmentComparison.equipmentComparison;

    // 会社名の要素を削除
    const updatedEquipmentComparison =
      equipmentComparison.filter((item) => item.companyName !== companyName);
    changeEstimateCheckListFormValue(
      "equipmentComparison.equipmentComparison",
      updatedEquipmentComparison
    );

    // 優位性を更新する
    const updatedSuperiority = estimateCheckListFormValue?.equipmentComparison?.superiority?.map((item) => {
      return item === `${companyName}が優位` ? "-" : item;
    });
    changeEstimateCheckListFormValue(
      "equipmentComparison.superiority",
      updatedSuperiority
    );
  };

  // #endregion

  // #region 保存前初期化

  /**
   * 保存前初期化
   * @returns エラー有無
   */
  const resetBeforeSave = () => {

    // 内製検討（内製検討なしの理由）
    if (estimateCheckListFormValue?.inHouseReview?.inHouseReview === inHouseReview.isTrue.value) {
      // 1. 内戦検討あり ⇨ 内製検討なしの理由 を初期化
      estimateCheckListFormValue.inHouseReview.inHouseReviewNoReason = null;
    }

    // 相見積（相見積なしの理由）
    if (estimateCheckListFormValue?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isTrue.value) {
      // 1. 相見積あり ⇨ 相見積なしの理由 を初期化
      estimateCheckListFormValue.competitiveEstimate.competitiveEstimateNoReason = null;
    }

    // 過去実績（直近購入時期）
    if (estimateCheckListFormValue?.pastPerformance?.pastPerformance === pastPerformance.isFalse.value) {
      // 1.過去実績なし ⇨ 直近購入時期 を初期化
      estimateCheckListFormValue.pastPerformance.purchaseDate = null;
    }

    // 過去実績（取得単価）
    if (estimateCheckListFormValue?.pastPerformance?.pastPerformance === pastPerformance.isFalse.value) {
      // 1.過去実績なし ⇨ 取得単価 を初期化
      estimateCheckListFormValue.pastPerformance.purchasePrice = null;
    }

    // 発注先（商社・代理店の理由）
    if (estimateCheckListFormValue?.supplier?.supplier === supplier.manufacturer.value) {
      // 1. 発注先が メーカー直販 ⇨ 商社・代理店の理由 を初期化
      estimateCheckListFormValue.supplier.distributorReason = null;
    }

    // 支払条件（調達物流部門担当役員の承認）
    if (estimateCheckListFormValue?.paymentType?.advancePayment === paymentType.isFalse.value) {
      // 1. 前払いなし ⇨ 調達物流部門担当役員の承認 を初期化
      estimateCheckListFormValue.paymentType.procurementApproval = null;
    }

    // 当社製品使用の確認/要請（使用製品名）
    const usageStatus = estimateCheckListFormValue?.ourProductUsage?.usageStatus ?? ourProductUsageStatus.used.value;
    if (usageStatus === ourProductUsageStatus.notApplicable.value && estimateCheckListFormValue?.ourProductUsage) {
      // 1. 使用状況が「対象外」 ⇨ 使用製品名 を初期化
      estimateCheckListFormValue.ourProductUsage.productName = null;
    }

    // 相見積による比較（特徴、課題）- 未入力の場合は「なし」を設定
    estimateCheckListFormValue?.equipmentComparison?.equipmentComparison.forEach((item) => {
      if (!item.features || item.features === "" || item.features === "-") {
        item.features = "なし";
      }
      if (!item.issue || item.issue === "" || item.issue === "-") {
        item.issue = "なし";
      }
    });

  }

  // #endregion

  // #region 必須チェック

  /**
   * 必須チェック
   * @param targetFormValue チェック対象のフォーム値（省略時は画面state値を使用）
   * @returns エラー有無
   */
  const requiredCheck = (targetFormValue: EstimateCheckListFormType = estimateCheckListFormValue): boolean => {

    // 関数宣言（エラー情報設定）
    const changeErrors = (name: string, errorMessage: string) => {
      setErrors((prevState) => ({
        ...prevState,
        [name]: { isError: true, errorMessage: `${errorMessage}` },
      }))
      isError = true;
    };

    // 変数宣言
    const requiredMessage = t('components.EstimateCheckList.validation.required');
    // 年月日 - フォーマットメッセージ
    const isValidDateFormatMessage = t('components.EstimateCheckList.validation.invalidDateFormat');
    // 年月日 - 有効範囲メッセージ
    const inputDateRangeMessage = t('components.EstimateCheckList.validation.dateRangeError', { start: formatDateByLocale(ValidDateRangeStart), end: formatDateByLocale(ValidDateRangeEnd) });
    // 文字数超過メッセージ
    const maxLengthMessage = t('components.EstimateCheckList.validation.maxLengthExceeded', { max: EstimateCheckListTextMaxLength });
    let isError = false;

    // エラーメッセージを初期化
    clearErrors();

    // 内製検討（内製検討なしの理由）
    if (targetFormValue?.inHouseReview?.inHouseReview === inHouseReview.isFalse.value
      && !targetFormValue?.inHouseReview?.inHouseReviewNoReason
    ) {
      // 1. 内戦検討なし and 内製検討なしの理由 が未記入
      changeErrors("inHouseReviewNoReason", `${requiredMessage}`);
    } else if ((targetFormValue?.inHouseReview?.inHouseReviewNoReason?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 内製検討なしの理由 が文字数超過
      changeErrors("inHouseReviewNoReason", `${maxLengthMessage}`);
    }

    // 相見積（相見積なしの理由）
    if (targetFormValue?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isFalse.value
      && !targetFormValue?.competitiveEstimate?.competitiveEstimateNoReason
    ) {
      // 1. 相見積なし and 相見積なしの理由 が未記入
      changeErrors("competitiveEstimateNoReason", `${requiredMessage}`);
    } else if ((targetFormValue?.competitiveEstimate?.competitiveEstimateNoReason?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 相見積なしの理由 が文字数超過
      changeErrors("competitiveEstimateNoReason", `${maxLengthMessage}`);
    }

    // 過去実績（直近購入時期）
    if (targetFormValue?.pastPerformance?.pastPerformance === pastPerformance.isTrue.value) {
      if (!targetFormValue?.pastPerformance?.purchaseDate) {
        // 1.過去実績あり and 直近購入時期 が未記入
        changeErrors("purchaseDate", `${requiredMessage}`);
      } else if (!isValidDateFormat(targetFormValue?.pastPerformance?.purchaseDate)) {
        // 2.過去実績あり and 直近購入時期 日付フォーマットがエラー
        changeErrors("purchaseDate", `${isValidDateFormatMessage}`);
      } else if (!isValidDateRange(new Date(targetFormValue?.pastPerformance?.purchaseDate))) {
        // 3.過去実績あり and 直近購入時期 日付有効範囲外
        changeErrors("purchaseDate", `${inputDateRangeMessage}`);
      }
    }

    // 過去実績（取得単価）
    if (targetFormValue?.pastPerformance?.pastPerformance === pastPerformance.isTrue.value
      && !targetFormValue?.pastPerformance?.purchasePrice
    ) {
      // 1.過去実績あり and 取得単価 が未記入
      changeErrors("purchasePrice", `${requiredMessage}`);
    }

    // 発注先（商社・代理店の理由）
    if (targetFormValue?.supplier?.supplier === supplier.distributor.value
      && !targetFormValue?.supplier?.distributorReason
    ) {
      // 1. 発注先が 商社・代理店 and 商社・代理店の理由 が未記入
      changeErrors("distributorReason", `${requiredMessage}`);
    } else if ((targetFormValue?.supplier?.distributorReason?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 商社・代理店の理由 が文字数超過
      changeErrors("distributorReason", `${maxLengthMessage}`);
    }

    // 価格交渉の回数（価格交渉の回数）
    if (!targetFormValue?.negotiation?.negotiationCount) {
      // 1. 価格交渉の回数 が未選択
      changeErrors("negotiationCount", `${requiredMessage}`);
    }

    // 価格交渉の回数（価格交渉の経緯）
    if (!targetFormValue?.negotiation?.negotiationBackground) {
      // 1. 価格交渉の経緯 が未記入
      changeErrors("negotiationBackground", `${requiredMessage}`);
    } else if ((targetFormValue?.negotiation?.negotiationBackground?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 価格交渉の経緯 が文字数超過
      changeErrors("negotiationBackground", `${maxLengthMessage}`);
    }

    // 支払条件（支払条件）
    if (!targetFormValue?.paymentType?.paymentBackground) {
      // 1. 支払条件 が未記入
      changeErrors("paymentBackground", `${requiredMessage}`);
    } else if ((targetFormValue?.paymentType?.paymentBackground?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 支払条件 が文字数超過
      changeErrors("paymentBackground", `${maxLengthMessage}`);
    }

    // 調達物流部門担当役員の承認（前払いありの場合のみ）
    if (targetFormValue?.paymentType?.advancePayment === paymentType.isTrue.value
      && !targetFormValue?.paymentType?.procurementApproval
    ) {
      // 1. 前払いあり and 調達物流部門担当役員の承認 が未選択
      changeErrors("procurementApproval", `${requiredMessage}`);
    }

    // 発注時期
    const orderDate = targetFormValue?.supplier?.orderDate;
    if (!orderDate) {
      // 1. 発注時期 が未記入
      changeErrors("orderDate", `${requiredMessage}`);
    } else if (!isValidDateFormat(orderDate)) {
      // 2. 発注時期 日付フォーマットがエラー
      changeErrors("orderDate", `${isValidDateFormatMessage}`);
    } else if (!isValidDateRange(new Date(orderDate))) {
      // 3. 発注時期 日付有効範囲外
      changeErrors("orderDate", `${inputDateRangeMessage}`);
    }

    // 当社製品使用の確認/要請（使用製品名）
    const usageStatus = targetFormValue?.ourProductUsage?.usageStatus ?? ourProductUsageStatus.used.value;
    if (usageStatus !== ourProductUsageStatus.notApplicable.value
      && !targetFormValue?.ourProductUsage?.productName
    ) {
      // 1. 使用状況が「対象外」以外 and 使用製品名 が未記入
      changeErrors("ourProductUsageProductName", `${requiredMessage}`);
    } else if ((targetFormValue?.ourProductUsage?.productName?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 使用製品名 が文字数超過
      changeErrors("ourProductUsageProductName", `${maxLengthMessage}`);
    }

    // 外注を使用せず購入する理由
    if (!targetFormValue?.purchaseWithoutOutsourcing?.reason) {
      // 1. 外注を使用せず購入する理由 が未記入
      changeErrors("purchaseWithoutOutsourcingReason", `${requiredMessage}`);
    } else if ((targetFormValue?.purchaseWithoutOutsourcing?.reason?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 外注を使用せず購入する理由 が文字数超過
      changeErrors("purchaseWithoutOutsourcingReason", `${maxLengthMessage}`);
    }

    const equipmentComparisonCheckResult = checkEquipmentComparison(targetFormValue);

    if (targetFormValue?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isTrue.value) {
      // 相見積：ありの場合の、相見積による比較
      if (targetFormValue?.equipmentComparison?.equipmentComparison.length === 0 || equipmentComparisonCheckResult.isRequiredError) {
        // 1. 相見積による比較 が0件
        // 2. 相見積による比較 の必須項目に未入力あり
        changeErrors("equipmentComparison", `${requiredMessage}`);
      }
    } else {
      // 相見積：なしの場合の、相見積による比較
      if (targetFormValue?.equipmentComparison?.equipmentComparison.length > 0 && equipmentComparisonCheckResult.isRequiredError) {
        // 1. 相見積による比較 が1件以上
        // 2. 相見積による比較 の必須項目に未入力あり
        changeErrors("equipmentComparison", `${requiredMessage}`);
      }
    }

    // 内製検討ありの場合のMMI内製チェック
    if (targetFormValue?.competitiveEstimate?.competitiveEstimate === competitiveEstimate.isTrue.value) {
      if (targetFormValue.inHouseReview.inHouseReview === inHouseReview.isTrue.value && !hasMMIInternal(targetFormValue)) {
        changeErrors("equipmentComparison", t('components.EstimateCheckList.validation.mmiInternalRequired'));
      }
    }

    // 会社名重複チェック（スプレッドシート連携時は画面の追加時重複チェックをバイパスするため）
    if (equipmentComparisonCheckResult.hasDuplicateCompanyName) {
      changeErrors("equipmentComparison", t('components.EstimateCheckList.message.duplicateCompany'));
    }

    // 選定理由（選定理由）
    if (!targetFormValue?.selectReason?.selectReason) {
      // 1. 選定理由 が未記入
      changeErrors("selectReason", `${requiredMessage}`);
    } else if ((targetFormValue?.selectReason?.selectReason?.length ?? 0) > EstimateCheckListTextMaxLength) {
      // 2. 選定理由 が文字数超過
      changeErrors("selectReason", `${maxLengthMessage}`);
    }

    // 相見積比較（会社名／特徴／課題）の文字数超過チェック
    if (equipmentComparisonCheckResult.overLengthFieldNames.length > 0) {
      // 項目ごとに上限文字数が異なるため、超過している項目名に上限文字数を付記して一覧表示（会社名は300文字、特徴／課題は1000文字）
      const fieldMaxLengthMap: { [fieldName: string]: number } = {
        companyName: COMPANYNAME_TEXT_MAX_LENGTH,
        features: EstimateCheckListTextMaxLength,
        issue: EstimateCheckListTextMaxLength,
      };
      const overLengthFieldLabels = equipmentComparisonCheckResult.overLengthFieldNames
        .map((fieldName) => t('components.EstimateCheckList.validation.fieldMaxLengthLabel', {
          field: t(`components.EstimateCheckList.tableRow.${fieldName}`),
          max: fieldMaxLengthMap[fieldName],
        }))
        .join(t('components.EstimateCheckList.validation.listSeparator'));
      changeErrors("equipmentComparisonMaxLength", t('components.EstimateCheckList.validation.equipmentComparisonMaxLengthExceeded', { fields: overLengthFieldLabels }));
    }

    return isError;

  }

  /**
   * "MMI内製"が存在するか判定
   * @param targetFormValue チェック対象のフォーム値（省略時は画面state値を使用）
   * @returns 判定結果
   */
  const hasMMIInternal = (targetFormValue: EstimateCheckListFormType = estimateCheckListFormValue): boolean => {

    // 比較表に"MMI内製"が存在するか判定
    const currentEquipmentComparison = targetFormValue.equipmentComparison.equipmentComparison;
    return currentEquipmentComparison.some(
      (company) => company.companyName === IN_HOUSE
    );

  };

  /**
   * 「相見積による比較」の必須チェック・文字数超過チェック・会社名重複チェック
   * @param targetFormValue チェック対象のフォーム値（省略時は画面state値を使用）
   * @returns 必須項目エラー有無（isRequiredError）、文字数超過している項目名一覧（overLengthFieldNames、重複なし）、会社名重複エラー有無（hasDuplicateCompanyName）
   */
  const checkEquipmentComparison = (targetFormValue: EstimateCheckListFormType = estimateCheckListFormValue): { isRequiredError: boolean, overLengthFieldNames: string[], hasDuplicateCompanyName: boolean } => {

    // チェック対象のフィールド一覧（features, issueは未入力許容）
    const requiredFields = [
      "basePrice", "unit", "optionPrice",
      "totalPrice", "deadline", "quality", "system", "repeat"
    ];

    // 文字数超過チェック対象のフィールドと上限文字数（スプレッドシート連携でのデータ取込時に画面入力欄のmaxLengthをバイパスして超過する可能性があるため）
    const maxLengthFields: { fieldName: string, maxLength: number }[] = [
      { fieldName: "companyName", maxLength: COMPANYNAME_TEXT_MAX_LENGTH },
      { fieldName: "features", maxLength: EstimateCheckListTextMaxLength },
      { fieldName: "issue", maxLength: EstimateCheckListTextMaxLength },
    ];

    // エラー情報を収集
    const newErrors: { [companyIndex: number]: { [fieldName: string]: boolean } } = {};
    let hasRequiredError = false;
    const overLengthFieldNameSet = new Set<string>();

    // 会社名ごとの登場回数をカウント（スプレッドシート連携時は画面の追加時重複チェックをバイパスするため）
    const companyNameCounts = new Map<string, number>();
    targetFormValue?.equipmentComparison?.equipmentComparison.forEach((item) => {
      if (!item.companyName) return;
      companyNameCounts.set(item.companyName, (companyNameCounts.get(item.companyName) ?? 0) + 1);
    });
    let hasDuplicateCompanyName = false;

    // 比較表に未入力・文字数超過の項目がないかチェック
    targetFormValue?.equipmentComparison?.equipmentComparison.forEach((item, companyIndex) => {
      newErrors[companyIndex] = {};
      requiredFields.forEach((fieldName) => {
        const value = item[fieldName as keyof typeof item];
        const isEmpty = value === "" || value === null || value === undefined || value === "-";
        if (isEmpty) {
          newErrors[companyIndex][fieldName] = true;
          hasRequiredError = true;
        } else {
          newErrors[companyIndex][fieldName] = false;
        }
      });
      // 比較表に文字数超過の項目がないかチェック（必須チェックの合否には含めない）
      maxLengthFields.forEach(({ fieldName, maxLength }) => {
        const value = item[fieldName as keyof typeof item];
        const isOverLength = (typeof value === "string" ? value.length : 0) > maxLength;
        newErrors[companyIndex][fieldName] = isOverLength;
        if (isOverLength) {
          overLengthFieldNameSet.add(fieldName);
        }
      });
      // 会社名が重複しているかチェック（文字数超過と同様、companyNameセルを赤枠ハイライト）
      const isDuplicate = (companyNameCounts.get(item.companyName) ?? 0) > 1;
      if (isDuplicate) {
        newErrors[companyIndex]["companyName"] = true;
        hasDuplicateCompanyName = true;
      }
    });

    // エラー状態を更新
    setEquipmentComparisonErrors(newErrors);

    return { isRequiredError: hasRequiredError, overLengthFieldNames: Array.from(overLengthFieldNameSet), hasDuplicateCompanyName };

  };

  // #endregion

  // ------------------------------
  // UI操作イベント
  // ------------------------------

  // #region 「見積チェックリスト」ボタン押下

  /**
   * 「見積チェックリスト」ボタン押下
   */
  const handleOpenCheckList = async () => {

    // 画面中央の座標を計算
    const centerX = (window.innerWidth / 2) - (window.innerWidth * (POPUP_WIDTH_RATIO / 100) / 2);
    const centerY = (window.innerHeight / 2) - (window.innerHeight * (POPUP_HEIGHT_RATIO / 100) / 2);
    setPosition({ x: centerX, y: centerY });

    // 画面表示
    setIsOpen(true);

  }

  // #endregion

  // #region 「追加」ボタン押下

  /**
   * 「追加」ボタン押下
   */
  const handleAddCompany = () => {

    // 会社名が未入力の場合はトースト表示
    if (!companyNameText.trim()) {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.companyNameEmpty'),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      return;
    }

    // 会社情報追加
    addCompany(companyNameText);

    // 会社名テキストボックスをクリア
    setCompanyNameText("");

  };

  // #endregion

  // #region 「一時保存」ボタン押下

  /**
   * 「一時保存」ボタン押下
   */
  const handleTempSave = () => {
    const eqName = (equipmentName ?? "unknown").replace(/[\\/:*?"<>|]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const json = JSON.stringify({ screenName: "EstimateCheckList", equipmentName: eqName, savedAt: new Date().toISOString(), data: estimateCheckListFormValue }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EstimateCheckList_${eqName}_${dateStr}.data`;
    a.click();
    URL.revokeObjectURL(url);
    toast({
      title: TITLE,
      description: t('components.EstimateCheckList.message.tempSaved'),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  /**
   * 「一時保存読み込み」ボタン押下
   */
  const handleTempLoad = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".data";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const parsed = JSON.parse(ev.target?.result as string);
          if (parsed.screenName !== "EstimateCheckList") {
            toast({ title: TITLE, description: t('components.EstimateCheckList.message.tempLoadScreenMismatch'), status: "error", duration: 4000, isClosable: true });
            return;
          }
          setEstimateCheckListFormValue(parsed.data);
          clearErrors();
          toast({ title: TITLE, description: t('components.EstimateCheckList.message.tempLoaded'), status: "success", duration: 3000, isClosable: true });
        } catch {
          toast({ title: TITLE, description: t('components.EstimateCheckList.message.tempLoadFailed'), status: "error", duration: 4000, isClosable: true });
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // #endregion

  // #region 「保存」ボタン押下

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {

    // 必須チェックエラーの場合はトースト表示
    if (requiredCheck()) {
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.validationFailed'),
        status: "warning",
        duration: 5000,
        isClosable: true,
      })
      return;
    }

    // 保存前に入力値を初期化
    resetBeforeSave();

    // 変数宣言
    const query = `
      mutation putCheckList($input: CheckList!) {
        putCheckList(input: $input) {status}
      }
    `;

    // APIリクエスト実行
    const result = await WebAPI.request({
      query: query,
      variables: {
        input: {
          projectId: projectId,
          equipmentId: equipmentId,
          estimateCheckList: JSON.stringify(estimateCheckListFormValue),
          updateDate: fetchedUpdateDate,
          ...(reviewDate && { reviewDate }),
        }
      }
    });

    console.log(`response = `, result);

    if (result?.data?.putCheckList?.status === "success") {

      // リクエスト成功
      toast({
        title: t('components.EstimateCheckList.message.saveSuccessTitle'),
        description: t('components.EstimateCheckList.message.saveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // チェックリスト更新状態
      setCheckListUpdateStatus?.(true);

      // 画面を閉じる（GoogleSpreadも閉じる）
      cleanupSpreadSheet();
      setIsOpen(false);

    } else if (result?.data?.putCheckList.status === 'expired') {

      // リクエスト失敗
      toast({
        title: TITLE,
        description: Messages.expiredMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      console.warn(`Conflict error. putCheckList.`);

    } else {

      // リクエスト失敗
      toast({
        title: TITLE,
        description: t('components.EstimateCheckList.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(`Failed to call API putCheckList. errors = `, result?.errors);

    }

  }

  // #endregion

  return (
    <>
      <Button onClick={handleOpenCheckList} m={1}>{t('components.EstimateCheckList.button.open')}</Button>

      {isOpen && (

        <Draggable defaultPosition={position} cancel="label,Input,textarea,select,button,table">

          <Box
            position={"fixed"}
            top={0}
            left={0}
            zIndex={1000}
            width={POPUP_WIDTH}
            height={POPUP_HEIGHT}
            bg={"white"}
            p={4}
            border={"1px solid #ccc"}
            borderRadius={"md"}
            shadow={"lg"}
            display={"flex"}
            flexDirection={"column"}
            overflow={"hidden"}
          >
            <Box width={"100%"} display={"flex"} flexDirection={"column"} height={"100%"} minHeight={0}>
              <Box flexShrink={0} mt={5} mx={10} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                <HStack>
                  <Text fontSize={20} fontWeight={"bold"}>{TITLE}</Text>
                  <HStack ml={4}>
                    <Text fontSize={14}>{t('components.EstimateCheckList.label.currency')}</Text>
                    <Select
                      size="sm"
                      w={120}
                      value={estimateCheckListFormValue?.currency?.currencyId ?? ""}
                      onChange={handleCurrencyChange}
                    >
                      {displayCurrencyData.map(currency => (
                        <option key={currency.currencyId} value={currency.currencyId}>
                          {currency.currencyName}
                        </option>
                      ))}
                    </Select>
                    {/* 為替レート表示 */}
                    <CurrencyList currencyData={displayCurrencyData} />
                  </HStack>
                </HStack>
                {/* 読み取り専用でも値コピー用にGoogleSpreadは開けるようにする（データ連携のみ不可） */}
                <HStack>
                  <ButtonGroup spacing={4} isAttached >
                    <Button
                      size="sm"
                      colorScheme={spreadSheetState.inputMode === "manual" ? "green" : "gray"}
                      onClick={handleManualClick}
                      isLoading={spreadSheetState.isLoading}
                    >
                      {t('components.EstimateCheckList.button.manualInput')}
                    </Button>
                    <Button
                      size="sm"
                      colorScheme={spreadSheetState.inputMode === "google" ? "green" : "gray"}
                      onClick={handleGoogleSpreadClick}
                      isLoading={spreadSheetState.isLoading}
                    >
                      {t('components.EstimateCheckList.button.googleSpread')}
                    </Button>
                  </ButtonGroup>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleDataLink}
                    isDisabled={!pagePermissions?.isEditable || spreadSheetState.inputMode !== "google" || !spreadSheetState.activeSpreadsheetId}
                    isLoading={spreadSheetState.isLoading}
                  >
                    {t('components.EstimateCheckList.button.dataLink')}
                  </Button>
                </HStack>
              </Box>
              <Box
                mt={5}
                flex={1}
                minHeight={0}
                overflow={"auto"}
              >
                <HStack
                  align={"stretch"}
                  spacing={0}
                  pointerEvents={spreadSheetState.inputMode === "google" ? "none" : "auto"}
                  opacity={spreadSheetState.inputMode === "google" ? 0.6 : 1}
                >

                  <Box ml={2} borderWidth={2} width={850}>

                    {/* 設備種類 */}
                    <FormControl isInvalid={false} m={10}>
                      <Flex align={"start"}>
                        <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.equipmentType')}</FormLabel>
                        <VStack align={"stretch"}>
                          <RadioGroup
                            value={estimateCheckListFormValue?.equipmentType.equipmentType}
                            onChange={(value => { changeEstimateCheckListFormValue("equipmentType.equipmentType", value) })}
                          >
                            <Radio minW={150} mr={6} isInvalid={false} value={equipmentType.genericEquipment.value}>{t('common.constants.equipmentUsageType.2')}</Radio>
                            <Radio minW={150} mr={6} isInvalid={false} value={equipmentType.dedicatedEquipment.value}>{t('common.constants.equipmentUsageType.1')}</Radio>
                          </RadioGroup>
                        </VStack>
                      </Flex>
                    </FormControl>

                    {/* リバースオークション有無 */}
                    <FormControl isInvalid={false} m={10}>
                      <Flex align={"start"}>
                        <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.reverseAuction')}</FormLabel>
                        <VStack align={"stretch"}>
                          <RadioGroup
                            value={estimateCheckListFormValue?.reverseAuction.reverseAuction}
                            onChange={(value => { changeEstimateCheckListFormValue("reverseAuction.reverseAuction", value) })}
                          >
                            <Radio minW={150} mr={6} isInvalid={false} value={reverseAuction.isTrue.value}>{reverseAuction.isTrue.name}</Radio>
                            <Radio minW={150} mr={6} isInvalid={false} value={reverseAuction.isFalse.value}>{reverseAuction.isFalse.name}</Radio>
                          </RadioGroup>
                        </VStack>
                      </Flex>
                    </FormControl>

                    {/* 内製検討 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.inHouseReview')}</FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.inHouseReview.inHouseReview}
                          onChange={(value => { changeEstimateCheckListFormValue("inHouseReview.inHouseReview", value) })}
                        >
                          <Radio width={150} mr={6} isInvalid={false} value={inHouseReview.isTrue.value} onChange={() => { addCompany(IN_HOUSE) }}>
                            {inHouseReview.isTrue.name}
                          </Radio>
                          <Radio width={150} mr={6} isInvalid={false} value={inHouseReview.isFalse.value} onChange={() => { removeCompany(IN_HOUSE) }}>
                            {inHouseReview.isFalse.name}
                          </Radio>
                        </RadioGroup>
                        {estimateCheckListFormValue?.inHouseReview.inHouseReview === inHouseReview.isFalse.value && (
                          <FormControl isInvalid={errors?.inHouseReviewNoReason?.isError}>
                            <Textarea
                              mt={2}
                              width={500}
                              maxLength={EstimateCheckListTextMaxLength}
                              placeholder={t('components.EstimateCheckList.placeholder.inHouseReviewNoReason')}
                              value={estimateCheckListFormValue?.inHouseReview.inHouseReviewNoReason || ""}
                              onChange={(e) => changeEstimateCheckListFormValue("inHouseReview.inHouseReviewNoReason", e.target.value)}
                            ></Textarea>
                            <FormErrorMessage style={formErrorMessageStyle}>{errors?.inHouseReviewNoReason?.errorMessage}</FormErrorMessage>
                          </FormControl>
                        )}
                      </VStack>
                    </Flex>

                    {/* 相見積 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.competitiveEstimate')}</FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.competitiveEstimate.competitiveEstimate}
                          onChange={(value => { changeEstimateCheckListFormValue("competitiveEstimate.competitiveEstimate", value) })}
                        >
                          <Radio minW={150} mr={6} isInvalid={false} value={competitiveEstimate.isTrue.value}>{competitiveEstimate.isTrue.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={competitiveEstimate.isFalse.value}>{competitiveEstimate.isFalse.name}</Radio>
                        </RadioGroup>
                        {estimateCheckListFormValue?.competitiveEstimate.competitiveEstimate === competitiveEstimate.isFalse.value && (
                          <FormControl isInvalid={errors?.competitiveEstimateNoReason?.isError}>
                            <Textarea
                              mt={2}
                              width={500}
                              maxLength={EstimateCheckListTextMaxLength}
                              placeholder={t('components.EstimateCheckList.placeholder.competitiveEstimateNoReason')}
                              value={estimateCheckListFormValue?.competitiveEstimate.competitiveEstimateNoReason || ""}
                              onChange={(e) => changeEstimateCheckListFormValue("competitiveEstimate.competitiveEstimateNoReason", e.target.value)}
                            ></Textarea>
                            <FormErrorMessage style={formErrorMessageStyle}>{errors?.competitiveEstimateNoReason?.errorMessage}</FormErrorMessage>
                          </FormControl>
                        )}
                      </VStack>
                    </Flex>

                    {/* 過去実績 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.pastPerformance')}</FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.pastPerformance.pastPerformance}
                          onChange={(value => { changeEstimateCheckListFormValue("pastPerformance.pastPerformance", value) })}
                        >
                          <Radio minW={150} mr={6} isInvalid={false} value={pastPerformance.isTrue.value}>{pastPerformance.isTrue.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={pastPerformance.isFalse.value}>{pastPerformance.isFalse.name}</Radio>
                        </RadioGroup>
                        {estimateCheckListFormValue?.pastPerformance.pastPerformance === pastPerformance.isTrue.value && (
                          <VStack align={"start"}>
                            <FormControl isInvalid={errors?.purchaseDate?.isError}>
                              <HStack mt={2}>
                                <Text width={140}>{t('components.EstimateCheckList.label.purchaseDate')}</Text>
                                <Input
                                  type={"date"}
                                  width={150}
                                  value={estimateCheckListFormValue?.pastPerformance.purchaseDate ?? ""}
                                  onChange={(e) => {
                                    if (e.target.value.length > 10) return;
                                    changeEstimateCheckListFormValue("pastPerformance.purchaseDate", e.target.value);
                                  }}
                                />
                              </HStack>
                              <FormErrorMessage ml={150} style={formErrorMessageStyle}>{errors?.purchaseDate?.errorMessage}</FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={errors?.purchasePrice?.isError}>
                              <HStack mt={2}>
                                <Text width={140}>{t('components.EstimateCheckList.label.purchasePrice')}</Text>
                                <CustomNumberInput
                                  w={150}
                                  min={0}
                                  max={999999999999}
                                  value={estimateCheckListFormValue?.pastPerformance.purchasePrice}
                                  onChange={(value) => changeEstimateCheckListFormValue("pastPerformance.purchasePrice", value)}
                                />
                                <Text>JPY</Text>
                              </HStack>
                              <FormErrorMessage ml={150} style={formErrorMessageStyle}>{errors?.purchasePrice?.errorMessage}</FormErrorMessage>
                            </FormControl>
                          </VStack>
                        )}
                      </VStack>
                    </Flex>

                    {/* 発注先 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.supplier')}</FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.supplier.supplier}
                          onChange={(value => { changeEstimateCheckListFormValue("supplier.supplier", value) })}
                        >
                          <Radio minW={150} mr={6} isInvalid={false} value={supplier.manufacturer.value}>{supplier.manufacturer.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={supplier.distributor.value}>{supplier.distributor.name}</Radio>
                        </RadioGroup>
                        {/* 発注時期 */}
                        <FormControl isInvalid={errors?.orderDate?.isError}>
                          <HStack mt={2}>
                            <Text width={140}>{t('components.EstimateCheckList.category.orderDate')}</Text>
                            <Input
                              type={"date"}
                              width={150}
                              value={estimateCheckListFormValue?.supplier?.orderDate ?? ""}
                              onChange={(e) => {
                                if (e.target.value.length > 10) return;
                                changeEstimateCheckListFormValue("supplier.orderDate", e.target.value);
                              }}
                            />
                          </HStack>
                          <FormErrorMessage ml={150} style={formErrorMessageStyle}>{errors?.orderDate?.errorMessage}</FormErrorMessage>
                        </FormControl>
                        {estimateCheckListFormValue?.supplier.supplier === supplier.distributor.value && (
                          <FormControl isInvalid={errors?.distributorReason?.isError}>
                            <Textarea
                              mt={2}
                              width={500}
                              height={100}
                              maxLength={EstimateCheckListTextMaxLength}
                              placeholder={t('components.EstimateCheckList.placeholder.distributorReason')}
                              value={estimateCheckListFormValue?.supplier.distributorReason || ""}
                              onChange={(e) => changeEstimateCheckListFormValue("supplier.distributorReason", e.target.value)}
                            ></Textarea>
                            <FormErrorMessage style={formErrorMessageStyle}>{errors?.distributorReason?.errorMessage}</FormErrorMessage>
                          </FormControl>
                        )}
                      </VStack>
                    </Flex>

                    {/* 価格交渉の回数 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle} display={"flex"} alignItems={"center"}>
                        {t('components.EstimateCheckList.category.negotiation')}
                        <Box ml={2} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                          <Popover placement={"top-end"}>
                            <PopoverTrigger>
                              <button style={{ background: 'none', border: 'none', padding: 0 }}>
                                <FaRegQuestionCircle size={"20px"} cursor={"pointer"} />
                              </button>
                            </PopoverTrigger>
                            <Portal>
                              <PopoverContent
                                width={"400px"}
                                bgColor={"#fffff0"}
                                boxShadow={"md"}
                                borderRadius={"8px"}
                                border={"1px solid #dcdcdc"}
                              >
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverBody>
                                  <VStack align={"start"} spacing={0} fontSize={"sm"}>
                                    <Text>{t('components.EstimateCheckList.help.negotiationNote')}</Text>
                                    <Flex align={"start"}>
                                      <Text>・</Text>
                                      <Text ml={5}>
                                        {t('components.EstimateCheckList.help.negotiationBullet1')}
                                      </Text>
                                    </Flex>
                                    <Flex align={"start"}>
                                      <Text>・</Text>
                                      <Text ml={5}>
                                        {t('components.EstimateCheckList.help.negotiationBullet2')}
                                      </Text>
                                    </Flex>
                                  </VStack>
                                </PopoverBody>
                              </PopoverContent>
                            </Portal>
                          </Popover>
                        </Box>
                      </FormLabel>
                      <VStack align={"stretch"}>
                        <FormControl isInvalid={errors?.negotiationCount?.isError}>
                          <HStack>
                            <CustomNumberInput
                              w={150}
                              min={0}
                              max={999999999999}
                              value={estimateCheckListFormValue?.negotiation.negotiationCount}
                              onChange={(value) => changeEstimateCheckListFormValue("negotiation.negotiationCount", value)}
                            />
                            <Text>{t('components.EstimateCheckList.label.times')}</Text>
                          </HStack>
                          <FormErrorMessage style={formErrorMessageStyle}>{errors?.negotiationCount?.errorMessage}</FormErrorMessage>
                        </FormControl>
                        <FormControl isInvalid={errors?.negotiationBackground?.isError}>
                          <Textarea
                            mt={2}
                            width={500}
                            maxLength={EstimateCheckListTextMaxLength}
                            placeholder={t('components.EstimateCheckList.placeholder.negotiationBackground')}
                            value={estimateCheckListFormValue?.negotiation.negotiationBackground || ""}
                            onChange={(e) => changeEstimateCheckListFormValue("negotiation.negotiationBackground", e.target.value)}
                          ></Textarea>
                          <FormErrorMessage style={formErrorMessageStyle}>{errors?.negotiationBackground?.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </VStack>
                    </Flex>

                    {/* 支払条件 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.paymentType')}</FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.paymentType.advancePayment}
                          onChange={(value => { changeEstimateCheckListFormValue("paymentType.advancePayment", value) })}
                        >
                          <Radio minW={150} mr={6} isInvalid={false} value={paymentType.isTrue.value}>{paymentType.isTrue.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={paymentType.isFalse.value}>{paymentType.isFalse.name}</Radio>
                        </RadioGroup>
                        <FormControl isInvalid={errors?.paymentBackground?.isError}>
                          <Textarea
                            mt={2}
                            width={500}
                            maxLength={EstimateCheckListTextMaxLength}
                            placeholder={t('components.EstimateCheckList.placeholder.paymentBackground')}
                            value={estimateCheckListFormValue?.paymentType.paymentBackground || ""}
                            onChange={(e) => changeEstimateCheckListFormValue("paymentType.paymentBackground", e.target.value)}
                          ></Textarea>
                          <FormErrorMessage style={formErrorMessageStyle}>{errors?.paymentBackground?.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </VStack>
                    </Flex>

                    {/* 調達物流部門担当役員の承認（前払いありの場合のみ表示） */}
                    {estimateCheckListFormValue?.paymentType.advancePayment === paymentType.isTrue.value && (
                      <Flex m={10} align={"start"}>
                        <FormLabel style={{ ...formLabelStyle, whiteSpace: "normal" }}>{t('components.EstimateCheckList.label.procurementApprovalLabel')}</FormLabel>

                        <VStack align={"stretch"}>
                          <FormControl isInvalid={errors?.procurementApproval?.isError}>
                            <RadioGroup
                              value={estimateCheckListFormValue?.paymentType.procurementApproval || ""}
                              onChange={(value => { changeEstimateCheckListFormValue("paymentType.procurementApproval", value) })}
                            >
                              <Radio minW={150} mr={6} isInvalid={false} value={procurementApproval.isTrue.value}>{procurementApproval.isTrue.name}</Radio>
                              <Radio minW={150} mr={6} isInvalid={false} value={procurementApproval.isFalse.value}>{procurementApproval.isFalse.name}</Radio>
                            </RadioGroup>
                            <FormErrorMessage style={formErrorMessageStyle}>{errors?.procurementApproval?.errorMessage}</FormErrorMessage>
                          </FormControl>
                        </VStack>
                      </Flex>
                    )}

                    {/* 当社製品使用の確認/要請 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={{ ...formLabelStyle, whiteSpace: "normal", minWidth: "250px" }}>
                        {t('components.EstimateCheckList.category.ourProductUsage')}<br />
                        {t('components.EstimateCheckList.label.mmProductUsage')}
                      </FormLabel>
                      <VStack align={"stretch"}>
                        <RadioGroup
                          value={estimateCheckListFormValue?.ourProductUsage?.usageStatus ?? ourProductUsageStatus.used.value}
                          onChange={(value => { changeEstimateCheckListFormValue("ourProductUsage.usageStatus", value) })}
                        >
                          <Radio minW={150} mr={6} isInvalid={false} value={ourProductUsageStatus.used.value}>{ourProductUsageStatus.used.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={ourProductUsageStatus.otherCompanyUsed.value}>{ourProductUsageStatus.otherCompanyUsed.name}</Radio>
                          <Radio minW={150} mr={6} isInvalid={false} value={ourProductUsageStatus.notApplicable.value}>{ourProductUsageStatus.notApplicable.name}</Radio>
                        </RadioGroup>
                        {(estimateCheckListFormValue?.ourProductUsage?.usageStatus ?? ourProductUsageStatus.used.value) !== ourProductUsageStatus.notApplicable.value && (
                          <FormControl isInvalid={errors?.ourProductUsageProductName?.isError}>
                            <Textarea
                              mt={2}
                              width={500}
                              maxLength={EstimateCheckListTextMaxLength}
                              placeholder={t('components.EstimateCheckList.placeholder.productName')}
                              value={estimateCheckListFormValue?.ourProductUsage?.productName || ""}
                              onChange={(e) => changeEstimateCheckListFormValue("ourProductUsage.productName", e.target.value)}
                            ></Textarea>
                            <FormErrorMessage style={formErrorMessageStyle}>{errors?.ourProductUsageProductName?.errorMessage}</FormErrorMessage>
                          </FormControl>
                        )}
                      </VStack>
                    </Flex>

                    {/* 外注を使用せず購入する理由 */}
                    <Flex m={10} align={"start"}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.purchaseWithoutOutsourcing')}</FormLabel>
                      <VStack align={"stretch"}>
                        <FormControl isInvalid={errors?.purchaseWithoutOutsourcingReason?.isError}>
                          <Textarea
                            width={500}
                            maxLength={EstimateCheckListTextMaxLength}
                            placeholder={t('components.EstimateCheckList.placeholder.purchaseWithoutOutsourcing')}
                            value={estimateCheckListFormValue?.purchaseWithoutOutsourcing?.reason || ""}
                            onChange={(e) => changeEstimateCheckListFormValue("purchaseWithoutOutsourcing.reason", e.target.value)}
                          ></Textarea>
                          <FormErrorMessage style={formErrorMessageStyle}>{errors?.purchaseWithoutOutsourcingReason?.errorMessage}</FormErrorMessage>
                        </FormControl>
                      </VStack>
                    </Flex>

                  </Box>

                  <Box mr={2} borderWidth={2} borderLeftWidth={0} width={"100%"}>

                    {/* 相見積による比較 */}
                    <Box m={10}>
                      <FormLabel style={formLabelStyle}>{t('components.EstimateCheckList.category.equipmentComparison')}</FormLabel>
                      <FormControl isInvalid={false}>
                        <Box display={"flex"} mt={5}>
                          <Input
                            type={"text"}
                            width={"30%"}
                            maxLength={COMPANYNAME_TEXT_MAX_LENGTH}
                            placeholder={t('components.EstimateCheckList.placeholder.companyName')}
                            size={"sm"}
                            onChange={(e) => setCompanyNameText(e.target.value)}
                            value={companyNameText}
                          ></Input>
                          <Button
                            ml={2}
                            size={"sm"}
                            onClick={handleAddCompany}
                          >{t('common.button.add')}</Button>
                        </Box>
                        <Box width={"auto"}>
                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length > 0 && (
                            <TableContainer mt={5}>
                              <Table
                                className={"mm-ceas-table"}
                                size={"sm"}
                                width={"auto"}
                                style={{
                                  tableLayout: "fixed",
                                  borderCollapse: "separate",
                                  borderSpacing: "0"
                                }}
                              >
                                <Thead>
                                  <Tr>
                                    <Th width={100}>{t('components.EstimateCheckList.label.tableItemName')}</Th>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Th
                                        maxWidth={300}
                                        style={{
                                          whiteSpace: "wrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          position: "relative",
                                          textTransform: "none",
                                          // グローバルCSS（.mm-ceas-table th）のborder指定より優先させるためインラインstyleで上書き
                                          ...(equipmentComparisonErrors[index]?.companyName
                                            ? { border: "2px solid #E53E3E" }
                                            : {}),
                                        }}
                                      >
                                        <Tooltip label={item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName} placement={"top"} hasArrow>
                                          {item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName}
                                        </Tooltip>
                                        <Button
                                          size={"sm"}
                                          ml={2}
                                          onClick={() => removeCompany(item.companyName)}
                                        >
                                          {t('common.button.delete')}
                                        </Button>
                                      </Th>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 && <Th width={100}>{t('components.EstimateCheckList.label.superiority')}</Th>}
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.basePrice')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <HStack>
                                          <CustomNumberInput
                                            size={"sm"}
                                            w={125}
                                            min={0}
                                            max={999999999999}
                                            value={item.basePrice ? Number(item.basePrice) : null}
                                            onChange={(value) => changeEquipmentComparJson(index, "basePrice", String(value ?? ""))}
                                            isInvalid={equipmentComparisonErrors[index]?.basePrice}
                                          />
                                          <Text>{estimateCheckListFormValue?.currency?.currencyName ?? "JPY"}</Text>
                                        </HStack>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[0] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(0, e.target.value)}
                                          cursor="pointer"
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.unit')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <HStack>
                                          <CustomNumberInput
                                            size={"sm"}
                                            w={125}
                                            min={0}
                                            max={999999999999}
                                            value={item.unit ? Number(item.unit) : null}
                                            onChange={(value) => changeEquipmentComparJson(index, "unit", String(value ?? ""))}
                                            isInvalid={equipmentComparisonErrors[index]?.unit}
                                          />
                                          <Text>{t('components.EstimateCheckList.label.units')}</Text>
                                        </HStack>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[1] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(1, e.target.value)}
                                          cursor="pointer"
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.optionPrice')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <HStack>
                                          <CustomNumberInput
                                            size={"sm"}
                                            w={125}
                                            min={0}
                                            max={999999999999}
                                            value={item.optionPrice ? Number(item.optionPrice) : null}
                                            onChange={(value) => changeEquipmentComparJson(index, "optionPrice", String(value ?? ""))}
                                            isInvalid={equipmentComparisonErrors[index]?.optionPrice}
                                          />
                                          <Text>{estimateCheckListFormValue?.currency?.currencyName ?? "JPY"}</Text>
                                        </HStack>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[2] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(2, e.target.value)}
                                          cursor="pointer"
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.features')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <Input
                                          type={"text"}
                                          size={"sm"}
                                          width={160}
                                          border={"1px solid #E2E8F0"}
                                          maxLength={EstimateCheckListTextMaxLength}
                                          value={item.features}
                                          placeholder={t('components.EstimateCheckList.placeholder.noInput')}
                                          isInvalid={equipmentComparisonErrors[index]?.features}
                                          onChange={(e) => changeEquipmentComparJson(index, "features", e.target.value)}
                                        ></Input>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[3] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(3, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.totalPrice')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <HStack>
                                          <CustomNumberInput
                                            size={"sm"}
                                            w={125}
                                            min={0}
                                            max={999999999999}
                                            value={item.totalPrice ? Number(item.totalPrice) : null}
                                            onChange={(value) => changeEquipmentComparJson(index, "totalPrice", String(value ?? ""))}
                                            isInvalid={equipmentComparisonErrors[index]?.totalPrice}
                                          />
                                          <Text>{estimateCheckListFormValue?.currency?.currencyName ?? "JPY"}</Text>
                                        </HStack>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[4] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(4, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  {/* 日本円換算額（選択通貨がJPY以外の場合のみ表示） */}
                                  {estimateCheckListFormValue?.currency?.currencyName !== "JPY" && (
                                    <Tr height={10}>
                                      <Td>{t('components.EstimateCheckList.tableRow.jpyAmount')}</Td>
                                      {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                        <Td key={index}>
                                          <HStack>
                                            <Text w={125} textAlign="right">
                                              {formatNumber(calculateJpyAmount(item.totalPrice), "JPY")}
                                            </Text>
                                            <Text>JPY</Text>
                                          </HStack>
                                        </Td>
                                      ))}
                                      {/* 優位性セレクトなし */}
                                      {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 && <Td></Td>}
                                    </Tr>
                                  )}
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.deadline')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <HStack>
                                          <CustomNumberInput
                                            size={"sm"}
                                            w={125}
                                            min={0}
                                            max={999999999999}
                                            value={item.deadline ? Number(item.deadline) : null}
                                            onChange={(value) => changeEquipmentComparJson(index, "deadline", String(value ?? ""))}
                                            isInvalid={equipmentComparisonErrors[index]?.deadline}
                                          />
                                          <Text>{t('components.EstimateCheckList.label.days')}</Text>
                                        </HStack>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[5] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(5, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.quality')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={item.quality}
                                          onChange={(e) => changeEquipmentComparJson(index, "quality", e.target.value)}
                                          cursor={"pointer"}
                                          borderColor={equipmentComparisonErrors[index]?.quality ? "#E53E3E" : undefined}
                                          borderWidth={equipmentComparisonErrors[index]?.quality ? "2px" : undefined}
                                        >
                                          {Object.values(equipmentComparisonItems.quality).map((quality) => (
                                            <option>
                                              {quality.name}
                                            </option>
                                          ))}
                                        </Select>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[6] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(6, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.system')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={item.system}
                                          onChange={(e) => changeEquipmentComparJson(index, "system", e.target.value)}
                                          cursor={"pointer"}
                                          borderColor={equipmentComparisonErrors[index]?.system ? "#E53E3E" : undefined}
                                          borderWidth={equipmentComparisonErrors[index]?.system ? "2px" : undefined}
                                        >
                                          {Object.values(equipmentComparisonItems.system).map((system) => (
                                            <option>
                                              {system.name}
                                            </option>
                                          ))}
                                        </Select>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[7] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(7, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.repeat')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={item.repeat}
                                          onChange={(e) => changeEquipmentComparJson(index, "repeat", e.target.value)}
                                          cursor={"pointer"}
                                          borderColor={equipmentComparisonErrors[index]?.repeat ? "#E53E3E" : undefined}
                                          borderWidth={equipmentComparisonErrors[index]?.repeat ? "2px" : undefined}
                                        >
                                          {Object.values(equipmentComparisonItems.repeat).map((repeat) => (
                                            <option>
                                              {repeat.name}
                                            </option>
                                          ))}
                                        </Select>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[8] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(8, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                  <Tr>
                                    <Td>{t('components.EstimateCheckList.tableRow.issue')}</Td>
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item, index) => (
                                      <Td key={index}>
                                        <Input
                                          type={"text"}
                                          size={"sm"}
                                          width={160}
                                          border={"1px solid #E2E8F0"}
                                          maxLength={EstimateCheckListTextMaxLength}
                                          value={item.issue}
                                          placeholder={t('components.EstimateCheckList.placeholder.noInput')}
                                          isInvalid={equipmentComparisonErrors[index]?.issue}
                                          onChange={(e) => changeEquipmentComparJson(index, "issue", e.target.value)}
                                        ></Input>
                                      </Td>
                                    ))}
                                    {estimateCheckListFormValue.equipmentComparison.equipmentComparison.length >= 2 &&
                                      <Td>
                                        <Select
                                          size={"sm"}
                                          w={160}
                                          value={estimateCheckListFormValue?.equipmentComparison?.superiority?.[9] ?? ""}
                                          onChange={(e) => changeSuperiorityElement(9, e.target.value)}
                                          cursor={"pointer"}
                                        >
                                          <option>-</option>
                                          {estimateCheckListFormValue.equipmentComparison.equipmentComparison.map((item) => (
                                            <option value={`${item.companyName}が優位`}>{t('components.EstimateCheckList.option.superiority', { companyName: item.companyName === IN_HOUSE ? t('components.EstimateCheckList.label.inHouseCompanyName') : item.companyName })}</option>
                                          ))}
                                        </Select>
                                      </Td>
                                    }
                                  </Tr>
                                </Tbody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                        {/* 会社が0件かつ相見積ありの場合にエラーメッセージを表示 */}
                        {errors?.equipmentComparison?.isError &&
                          estimateCheckListFormValue.equipmentComparison.equipmentComparison.length === 0 &&
                          estimateCheckListFormValue.competitiveEstimate.competitiveEstimate === competitiveEstimate.isTrue.value && (
                            <Text color="#E53E3E" fontWeight="bold" mt={2}>{t('components.EstimateCheckList.validation.companyRequired')}</Text>
                          )}
                        {/* 個別エラーがある場合にエラーメッセージを表示（companyName, features, issueの文字数超過は対象外） */}
                        {Object.values(equipmentComparisonErrors).some(companyErrors =>
                          Object.entries(companyErrors).some(([fieldName, hasError]) => hasError && fieldName !== "companyName" && fieldName !== "features" && fieldName !== "issue")
                        ) && (
                            <Text color="#E53E3E" fontWeight="bold" mt={2}>{t('components.EstimateCheckList.validation.required')}</Text>
                          )}
                        {errors?.equipmentComparison?.errorMessage &&
                          errors?.equipmentComparison?.errorMessage !== t('components.EstimateCheckList.validation.required') && (
                            <Text color="#E53E3E" fontWeight="bold" mt={2}>{errors?.equipmentComparison?.errorMessage}</Text>
                          )}
                        {/* 特徴／課題の文字数超過エラーを表示（超過している項目名を一覧表示。該当セルは赤枠ハイライトで明示） */}
                        {errors?.equipmentComparisonMaxLength?.isError && (
                          <Text color="#E53E3E" fontWeight="bold" mt={2}>{errors?.equipmentComparisonMaxLength?.errorMessage}</Text>
                        )}
                      </FormControl>
                    </Box>

                    {/* 選定理由 */}
                    <Box m={10}>
                      <FormControl isInvalid={errors?.selectReason?.isError}>
                        <Flex align={"start"}>
                          <FormLabel style={formLabelStyle} display={"flex"} alignItems={"center"}>
                            {t('components.EstimateCheckList.category.selectReason')}
                            <Box ml={2} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                              <Popover placement={"top-end"}>
                                <PopoverTrigger>
                                  <button style={{ background: 'none', border: 'none', padding: 0 }}>
                                    <FaRegQuestionCircle size={"20px"} cursor={"pointer"} />
                                  </button>
                                </PopoverTrigger>
                                <Portal>
                                  <PopoverContent
                                    width={"420px"}
                                    bgColor={"#fffff0"}
                                    boxShadow={"md"}
                                    borderRadius={"8px"}
                                    border={"1px solid #dcdcdc"}
                                  >
                                    <PopoverArrow />
                                    <PopoverCloseButton />
                                    <PopoverBody>
                                      <VStack align={"start"} spacing={0} fontSize={"sm"}>
                                        <Text>{t('components.EstimateCheckList.help.selectReasonNote')}</Text>
                                        <Flex align={"start"}>
                                          <Text>・</Text>
                                          <Text ml={5}>
                                            {t('components.EstimateCheckList.help.selectReasonBullet1')}
                                          </Text>
                                        </Flex>
                                        <Flex align={"start"}>
                                          <Text>・</Text>
                                          <Text ml={5}>
                                            {t('components.EstimateCheckList.help.selectReasonBullet2')}
                                          </Text>
                                        </Flex>
                                        <Flex align={"start"}>
                                          <Text>・</Text>
                                          <Text ml={5}>
                                            {t('components.EstimateCheckList.help.selectReasonBullet3')}
                                          </Text>
                                        </Flex>
                                        <Flex align={"start"}>
                                          <Text>・</Text>
                                          <Text ml={5}>
                                            {t('components.EstimateCheckList.help.selectReasonBullet4')}
                                          </Text>
                                        </Flex>
                                      </VStack>
                                    </PopoverBody>
                                  </PopoverContent>
                                </Portal>
                              </Popover>
                            </Box>
                          </FormLabel>
                        </Flex>
                        <VStack align={"stretch"}>
                          <Textarea
                            mt={2}
                            width={500}
                            maxLength={EstimateCheckListTextMaxLength}
                            placeholder={t('components.EstimateCheckList.placeholder.selectReason')}
                            value={estimateCheckListFormValue?.selectReason.selectReason || ""}
                            onChange={(e) => changeEstimateCheckListFormValue("selectReason.selectReason", e.target.value)}
                          ></Textarea>
                        </VStack>
                        <FormErrorMessage style={formErrorMessageStyle}>{errors?.selectReason?.errorMessage}</FormErrorMessage>
                      </FormControl>
                    </Box>

                  </Box>

                </HStack>
              </Box>
              <Box flexShrink={0} mt={2} mx={2} display={"flex"} justifyContent={"flex-end"}>
                {pagePermissions?.isEditable && (
                  <>
                    <Button colorScheme="teal" variant="outline" ml={1} mr={1} onClick={handleTempSave}>{t('components.EstimateCheckList.button.tempSave')}</Button>
                    <Button colorScheme="teal" variant="outline" ml={1} mr={1} onClick={handleTempLoad}>{t('components.EstimateCheckList.button.tempLoad')}</Button>
                  </>
                )}
                <Button colorScheme="blue" ml={1} mr={1} isDisabled={!pagePermissions?.isEditable} onClick={handleSave}>{t('components.EstimateCheckList.button.save')}</Button>
                <Button onClick={() => { cleanupSpreadSheet(); setIsOpen(false) }} aria-label="Close popup">{t('components.EstimateCheckList.button.close')}</Button>
              </Box>

            </Box>
          </Box>
        </Draggable >

      )}

      <ConfirmModal
        isOpen={showRegenerateConfirm}
        title={t('components.EstimateCheckList.message.confirmRegenerateTitle')}
        message={t('components.EstimateCheckList.message.confirmRegenerateBody')}
        okLabel={t('components.EstimateCheckList.message.confirmRegenerateOk')}
        cancelLabel={t('components.EstimateCheckList.message.confirmRegenerateCancel')}
        onOk={handleConfirmRegenerate}
        onCancel={() => setShowRegenerateConfirm(false)}
      />

    </>
  );

}

export default EstimateCheckList;